import { CubePresenter, Logger, NOOP_VOID } from 'wdc-cube';
import { csvBlob, csvDecimal, formatCount, formatDate, formatDecimal, formatDuration, formatKm, formatPercent } from '../../../../domain';
import { FileSaver } from '../../../../infra';
import { UserBaseApi } from './user-base.api';
import { USER_BASE_SHORTCUTS, intervalOf } from './user-base.types';
import { CompanySelectorPresenter, MISSING_COMPANY } from '../../../../components/company-selector/company-selector.presenter';
import { PeriodSelectorPresenter, recallPeriod } from '../../../../components/period';
import { UserBaseKeys } from './user-base.key';
import { CollaboratorsKeys } from '../collaborators/collaborators.key';
import { DeviceSourceScope, formatDaysPerCharge, FunnelStepScope, KpiScope, OverviewPointScope, PhysicalPointScope, TrainingPointScope, TrainingTypeScope, UserBaseScope } from './user-base.scope';
const LOG = Logger.get('Mormaii.UserBasePresenter');
// @Inject
const api = UserBaseApi.INSTANCE;
/**
 * Todo cartao daqui conta SO conta ativa, e a lista de Colaboradores nao.
 *
 * As consultas desta tela filtram `u.active = true` em toda contagem de pessoas. Sem
 * repetir isso no recorte, "Ja registraram treino" dizia 16 e a lista que ele abre
 * trazia 17 - conferido contra a base de QA em 19/09/2026: uma conta desligada que
 * continuou treinando. Vale para todos, e nao so para aquele: os outros batiam por
 * acaso, porque nenhum desativado tinha registro naquelas janelas.
 */
const SO_CONTAS_ATIVAS = true;
/**
 * O periodo do relatorio, como recorte de CADASTRO - o de "Novos cadastros".
 *
 * `registeredBetween` no servidor compara `user.created_date`, que e a mesma coluna de
 * `sqlRegistrationSeries`. E por isso que os dois numeros podem bater.
 */
function registrationReach(inicio, fim) {
    return {
        registeredFrom: inicio,
        registeredTo: fim,
        // Ver `CollaboratorReach.activeAccountsOnly`: este cartao conta tambem quem foi
        // desativado, porque a consulta que o produz nao filtra `u.active`.
        activeAccountsOnly: false
    };
}
/**
 * `2026-09-19` mais ou menos N dias, sem passar pelo fuso.
 *
 * Meio-dia como ancora, que e o que `periodRange` faz pela mesma razao: com meia-noite,
 * uma hora de diferenca de fuso empurra a data para a vespera.
 */
function shiftDays(iso, days) {
    if (!iso)
        return iso;
    const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
    const base = new Date(year, month - 1, day, 12);
    base.setDate(base.getDate() + days);
    const dois = (value) => String(value).padStart(2, '0');
    return `${base.getFullYear()}-${dois(base.getMonth() + 1)}-${dois(base.getDate())}`;
}
/**
 * Quantos dias tem o intervalo - e, desde 23/09/2026, a JANELA de atividade.
 *
 * "Ativo" passou a querer dizer "registrou algo entre as duas datas". Antes a janela era um
 * controle a parte, com 7, 15 ou 30 dias: um intervalo de 90 dias convivia com um cartao
 * escrito "Ativos (30 dias)", e ninguem tinha como saber, olhando a tela, que aqueles 30 nao
 * eram os 90 do grafico ao lado.
 *
 * O servidor continua recebendo `activeWindowDays` e nao mudou: ele calcula
 * `windowStartTs = fim + 1 - janela`, que com a janela do tamanho do intervalo da exatamente
 * o inicio dele.
 */
function daysInInterval(startDate, endDate) {
    if (!startDate || !endDate) {
        return 1;
    }
    // Meio-dia como ancora, pela mesma razao de `periodRange`: com meia-noite, uma hora de
    // fuso empurra a data para a vespera e a conta perde um dia.
    const inicio = new Date(`${startDate}T12:00:00`);
    const fim = new Date(`${endDate}T12:00:00`);
    const dias = Math.round((fim.getTime() - inicio.getTime()) / 86_400_000) + 1;
    return dias > 0 ? dias : 1;
}
/**
 * Adocao e Engajamento: crescimento, adocao de treino e atividade fisica.
 *
 * Tres endpoints independentes, buscados em PARALELO - em serie a tela esperaria a
 * soma dos tres em vez do maior deles.
 *
 * O periodo tem duas formas de ser dito: por atalho (`30d`, `12m`) ou por intervalo
 * digitado. Um deriva do outro, e o endereco carrega so o que nao da para deduzir -
 * ver `UserBaseKeys`.
 */
export class UserBasePresenter extends CubePresenter {
    constructor(app) {
        super(app, new UserBaseScope());
        this.parentSlot = NOOP_VOID;
        this.at = {
            preset: '30d',
            startDate: '',
            endDate: '',
            granularity: 'DAY'
        };
        /** O seletor de empresa - ver `CompanySelectorPresenter`. */
        this.selector = new CompanySelectorPresenter(this.update, async (companyId) => {
            this.owner?.rememberFilterCompany(companyId);
            await this.go({ companyId });
        }, () => this.app.alert('error', MISSING_COMPANY.title, MISSING_COMPANY.text));
        /**
         * O seletor de periodo.
         *
         * Sem teto: esta tela le agregados, e um ano de agregado diario e uma consulta
         * que o servidor responde. O Relatorio de Sono tem teto porque le noite a noite.
         */
        this.period = new PeriodSelectorPresenter(this.update, USER_BASE_SHORTCUTS, async ({ from, to, shortcut }) => {
            const custom = shortcut === 'custom';
            await this.go({
                preset: shortcut,
                startDate: from,
                endDate: to,
                // Trocar de atalho pode implicar outra granularidade; o `custom`
                // preserva a que estava, porque quem digitou datas nao disse nada
                // sobre agregacao.
                granularity: custom
                    ? this.at.granularity
                    : (intervalOf(shortcut)?.granularity ?? this.at.granularity)
            });
        }, undefined, true);
        this.pending = 0;
    }
    async applyParameters(intent, initialization, last) {
        const keys = new UserBaseKeys(this.app, intent);
        // A guarda de papel, que no painel antigo era o `ProtectRouter` repetido em
        // cada rota. Antes de qualquer rede: recusada, a tela nem chega a montar.
        if (!(await keys.owner?.allowEntry('user-base', intent))) {
            return false;
        }
        // Atributos da intencao: lidos a cada chamada, como os slots. Vem de quem esta
        // acima na composicao, e reler custa nada.
        this.owner = keys.owner;
        if (initialization) {
            this.scope.onGranularityChanged = this.handleGranularityChanged.bind(this);
            this.scope.onCompanyChanged = this.handleCompanyChanged.bind(this);
            this.scope.onRefresh = this.action(this.onRefresh);
            this.scope.onExportOverview = this.action(this.onExportOverview);
            this.scope.onExportTrainingTypes = this.action(this.onExportTrainingTypes);
            this.scope.onExportTrainingSeries = this.action(this.onExportTrainingSeries);
            this.scope.onExportPhysical = this.action(this.onExportPhysical);
            this.scope.onExportDevices = this.action(this.onExportDevices);
            // O seletor proprio so existe quando a escolha NAO esta no cabecalho.
            this.scope.companyChooser =
                (keys.owner?.canChooseCompany ?? false) && !(keys.owner?.hasHeaderChooser ?? false);
            // So quem escolhe empresa paga a busca dela: antes esta tela trazia a
            // lista INTEIRA mesmo para o admin de empresa, que nao tem seletor.
            if (this.scope.companyChooser) {
                this.selector.describe('Empresa', 'Todas as empresas');
                /*
                 * Quem administra empresas escolhe entre as DELE; o administrador
                 * geral escolhe entre todas, e ai o seletor busca no servidor.
                 */
                const minhas = keys.owner?.myCompanies ?? [];
                if (minhas.length > 0) {
                    this.selector.restrictTo(minhas);
                }
                await this.selector.showSelected(keys.owner?.companyOnArrival(keys.companyId));
            }
            LOG.info('Initialized');
        }
        // O slot e decidido acima na composicao e pode mudar entre viagens: le-se sempre.
        this.parentSlot = keys.parentSlot;
        if (last) {
            this.owner?.showSection('user-base', 'Adoção e Engajamento', 'Cadastro, engajamento, treinos e desempenho físico, em visão diária ou mensal');
        }
        this.parentSlot(this.scope);
        // Sem periodo no endereco, o ultimo escolhido no painel (B-35), com a
        // granularidade que aquele atalho implica.
        const lembrado = keys.periodInAddress ? undefined : recallPeriod(USER_BASE_SHORTCUTS);
        await this.moveTo({
            preset: (lembrado?.shortcut ?? keys.preset),
            startDate: lembrado?.from ?? keys.startDate,
            endDate: lembrado?.to ?? keys.endDate,
            granularity: lembrado
                ? (intervalOf(lembrado.shortcut)?.granularity ?? keys.granularity)
                : keys.granularity,
            // Sem empresa no endereco, a PADRAO de quem esta logado: quem
            // administra mais de uma precisa dizer qual, e o servidor recusa
            // agir sem saber.
            companyId: keys.owner?.companyOnArrival(keys.companyId)
        });
        return true;
    }
    publishParameters(intent) {
        const keys = new UserBaseKeys(this.app, intent);
        // O atalho primeiro: os setters de data e granularidade consultam o preset
        // para decidir se precisam aparecer no endereco.
        keys.preset = this.at.preset;
        keys.startDate = this.at.startDate;
        keys.endDate = this.at.endDate;
        keys.granularity = this.at.granularity;
        keys.companyId = this.companyIdToPublish;
    }
    /** Ver `RestrictedPresenter.companyIdInAddress`: com cabecalho, quem publica e ele. */
    get companyIdToPublish() {
        return this.owner?.companyIdInAddress(this.at.companyId);
    }
    async go(change) {
        await this.moveTo({ ...this.at, ...change });
        this.updateHistory();
    }
    async moveTo(target) {
        this.showRequest(target);
        const query = {
            startDate: target.startDate,
            endDate: target.endDate,
            granularity: target.granularity,
            companyId: target.companyId ?? null,
            // A janela E o intervalo - ver `daysInInterval`.
            activeWindowDays: daysInInterval(target.startDate, target.endDate)
        };
        const signature = JSON.stringify(query);
        if (signature === this.lastQuery) {
            this.at = target;
            return;
        }
        const token = ++this.pending;
        this.scope.loading = true;
        this.scope.error = undefined;
        try {
            // Os quatro em paralelo: nenhum depende do outro.
            const [overview, training, physical, devices] = await Promise.all([
                api.fetchOverview(query),
                api.fetchTraining(query),
                api.fetchPhysical(query),
                // Os vestiveis nao derrubam a tela: uma falha so esconde a secao deles.
                api.fetchDevices(query).catch((caught) => {
                    LOG.warn('Sem o painel dos vestiveis', caught);
                    return undefined;
                })
            ]);
            if (token !== this.pending) {
                return;
            }
            this.lastQuery = signature;
            this.at = target;
            this.showOverview(overview);
            this.showTraining(training);
            this.showPhysical(physical);
            this.showDevices(devices);
        }
        catch (caught) {
            if (token !== this.pending) {
                return;
            }
            this.scope.error = caught instanceof Error ? caught.message : 'Não foi possível carregar os indicadores.';
            this.showRequest(this.at);
        }
        finally {
            if (token === this.pending) {
                this.scope.loading = false;
            }
        }
    }
    showRequest(target) {
        this.scope.preset = target.preset;
        this.scope.granularity = target.granularity;
        this.scope.activeWindowDays = daysInInterval(target.startDate, target.endDate);
        this.scope.customRange = target.preset === 'custom';
        // O seletor calcula o intervalo e move o proprio scope.
        this.period.show(target.preset, target.startDate, target.endDate);
        this.scope.period = this.period.scope;
        this.buildCompanies(target);
    }
    /**
     * As empresas do seletor do topo.
     *
     * Lista simples, e nao o `FilterScope` de Colaboradores: aqui o seletor e um
     * `<select>` NATIVO dentro da barra roxa, com `[&>option]:text-gray-800` - a
     * referencia nao usa o `Select` do radix nesta tela.
     */
    buildCompanies(target) {
        this.scope.companySelector = this.scope.companyChooser ? this.selector.scope : undefined;
        void target;
    }
    // ========== OS TRES PAINEIS ==========
    showOverview(overview) {
        const summary = overview.summary;
        const period = overview.period;
        this.scope.hasOverview = true;
        this.scope.periodStart = period?.startDate ?? '';
        this.scope.periodEnd = period?.endDate ?? '';
        this.scope.granularityDescription = period?.granularityDescription ?? '';
        this.scope.companyName = period?.companyName ?? undefined;
        this.scope.generatedAt = period?.generatedAt ?? '';
        this.scope.neverSyncedUsers = summary.neverSyncedUsers ?? 0;
        const monthly = (period?.granularity ?? 'DAY') === 'MONTH';
        // Quem manda no rotulo e a RESPOSTA: e o numero de dias que o servidor usou.
        const days = period?.activeWindowDays ?? daysInInterval(this.at.startDate, this.at.endDate);
        const activeBase = summary.totalActiveUsers ?? 0;
        // O periodo que a RESPOSTA descreve - e dele que os recortes dos cartoes saem.
        const inicio = period?.startDate ?? this.at.startDate;
        const fim = period?.endDate ?? this.at.endDate;
        /*
         * Os marcos do funil alimentam TRES cartoes de lacuna, e nao so as barras.
         *
         * O funil mostra o que ja foi feito; os cartoes mostram o que FALTA, que e onde se
         * age. Nenhum deles custa consulta: os quatro numeros ja vinham na resposta, e o que
         * faltava era uma subtracao.
         */
        const marcos = overview.funnel;
        const cadastrados = marcos?.registered ?? 0;
        const semAnamnese = cadastrados - (marcos?.withAnamnese ?? 0);
        const parados = (marcos?.everSynced ?? 0) - (marcos?.activeInWindow ?? 0);
        /** A fatia de `cadastrados` que um numero representa - o comparavel entre eles. */
        const daBase = (valor) => (cadastrados > 0 ? formatPercent((valor * 100) / cadastrados) : undefined);
        /*
         * Os seis indicadores, separados pelo que cada um LE do filtro.
         *
         * RETRATO na data de referencia: o total de colaboradores, que corta no fim, e os
         * conectados do ULTIMO balde. Nenhum dos dois usa a data inicial.
         *
         * FLUXO entre as duas datas: ativos, logins, novos cadastros e o pico.
         *
         * "Ativos" e "Logins" mudaram de grupo em 23/09/2026, e por consequencia: enquanto a
         * janela era um controle a parte (7, 15 ou 30 dias contados da referencia), os dois
         * eram retrato. Com a janela sendo o proprio intervalo, eles passaram a responder
         * "quem registrou algo ENTRE as duas datas" - que e fluxo, e e o que o rotulo agora
         * diz: "Ativos (90 dias)" num relatorio de 90 dias.
         */
        const retrato = [
            this.buildKpi({
                label: 'Total de colaboradores',
                value: formatCount(summary.totalUsers),
                subtitle: `${formatCount(summary.totalActiveUsers)} com cadastro ativo`,
                help: 'Todos os cadastros até a data final do filtro.',
                icon: 'users',
                /*
                 * Ganhou caminho em 23/09/2026, e por duas mudancas que vieram antes: o
                 * numero deixou de contar CONTAS (22/09) e a lista ganhou o filtro de periodo
                 * de CADASTRO (23/09). Sem uma das duas, o enlace era impossivel.
                 *
                 * `activeAccountsOnly: false` porque este cartao conta TODOS - e o unico
                 * indicador da tela que nao exige cadastro ativo. O degrau "Cadastrados" do
                 * funil, logo abaixo, conta so os ativos e abre a lista com o recorte dele.
                 */
                opens: {
                    hint: 'Ver todos os colaboradores cadastrados até esta data',
                    criteria: { registeredTo: fim, activeAccountsOnly: false }
                }
                /*
                 * "Colaboradores" e nao "usuarios", desde 22/09/2026: sao conceitos
                 * diferentes, e o cartao conta um deles. `countUsersUntil` ganhou
                 * `JOIN patient` e passou a contar quem tem FICHA - 88 onde antes dizia
                 * 116 -, que e o que o funil logo abaixo sempre contou. As 28 de
                 * diferenca administram ou atendem: usam o painel, e nao sao medidas.
                 *
                 * Usuario continua existindo como palavra nesta tela, e num cartao so:
                 * "Logins", que conta CONTAS porque login e da conta e nao da ficha.
                 */
            }),
            this.buildKpi({
                label: monthly ? 'Conectados (mês)' : 'Conectados (dia)',
                value: formatCount(summary.connectedLastBucket),
                subtitle: `Média diária: ${formatDecimal(summary.averageConnectedPerDay)}`,
                help: 'Colaboradores distintos que registraram dados no período.',
                icon: 'calendar-check',
                accent: 'sky',
                /*
                 * O ULTIMO balde, e nao o periodo inteiro: e o que o numero conta. No
                 * dia, o dia final; no mes, do primeiro dia daquele mes ate o fim - que
                 * e onde o `date_trunc('month')` da consulta corta.
                 */
                opens: {
                    hint: monthly ? 'Ver quem registrou algo no mês' : 'Ver quem registrou algo no dia',
                    criteria: {
                        activeFrom: monthly ? `${fim.slice(0, 7)}-01` : fim,
                        activeTo: fim
                    }
                }
            }),
            this.buildKpi({
                label: 'Nunca registraram',
                value: formatCount(summary.neverSyncedUsers),
                subtitle: daBase(summary.neverSyncedUsers ?? 0)
                    ? `${daBase(summary.neverSyncedUsers ?? 0)} dos cadastrados`
                    : undefined,
                help: 'Colaboradores que nunca registraram medição nem treino, até a data de referência.',
                icon: 'account-remove',
                accent: 'rose',
                /*
                 * Ganhou caminho em 23/09/2026, quando a lista aprendeu a dizer "NAO
                 * registrou": ate ali o filtro de la era so positivo, e a negacao de um
                 * `EXISTS` nao existia no servidor.
                 *
                 * Sem data inicial de proposito - "nunca" nao tem comeco. A ausencia de
                 * `activeFrom` e o que faz o recorte ser "nada ate o fim do periodo", e nao
                 * "nada no intervalo".
                 */
                opens: {
                    hint: 'Ver quem nunca registrou medição nem treino',
                    criteria: { activeTo: fim, activityPresence: 'ABSENT', registeredTo: fim }
                }
            }),
            this.buildKpi({
                label: 'Sem anamnese',
                value: formatCount(semAnamnese),
                subtitle: daBase(semAnamnese) ? `${daBase(semAnamnese)} dos cadastrados` : undefined,
                help: 'Colaboradores com cadastro ativo cuja ficha ainda não tem anamnese preenchida.',
                icon: 'account-edit',
                accent: 'amber',
                /*
                 * Ganhou caminho em 23/09/2026, quando a lista passou a filtrar por anamnese.
                 *
                 * O recorte e o do cartao: cadastrados ate a data de referencia, com conta
                 * ativa, sem anamnese. O numero do cartao e uma SUBTRACAO sobre pessoas e a
                 * lista mostra FICHAS - enquanto cada pessoa tem uma ficha os dois batem, e o
                 * dia em que alguem tiver duas a lista mostra a que falta. Ver
                 * `FiltroDeAnamneseBateComOFunilTest`, no servidor.
                 */
                opens: {
                    hint: 'Ver quem ainda não preencheu a anamnese',
                    criteria: { registeredTo: fim, anamnese: 'MISSING' }
                }
            })
        ];
        /*
         * "Logins" SAIU daqui em 23/09/2026, e foi para "Acesso ao painel".
         *
         * Ele contava CONTAS numa tela que fala de colaboradores - e pior, mudava de
         * populacao conforme o filtro: sem empresa escolhida contava todo mundo, inclusive
         * quem administra a plataforma; com empresa, passava a exigir ficha. Medido na base
         * local em 23/09: das 40 contas com sessao guardada, 30 eram de colaborador entrando
         * pelo APP - tres quartos do numero nada tinham a ver com acesso ao painel.
         *
         * O servidor continua devolvendo `loggedInWindow` no payload: o painel ANTIGO, que
         * esta no ar, ainda mostra aquele cartao. O campo some quando ele sair.
         */
        const fluxo = [
            this.buildKpi({
                label: `Ativos (${days} dias)`,
                value: formatCount(summary.activeUsersInWindow),
                subtitle: activeBase > 0
                    ? `${formatPercent(((summary.activeUsersInWindow ?? 0) * 100) / activeBase)} da base ativa`
                    : undefined,
                help: 'Colaboradores que registraram medição ou treino dentro da janela escolhida.',
                icon: 'activity',
                accent: 'emerald',
                opens: {
                    hint: `Ver quem registrou algo nos últimos ${days} dias`,
                    criteria: this.activeWindow(days, fim)
                }
            }),
            this.buildKpi({
                label: 'Parados',
                value: formatCount(parados),
                subtitle: 'já registraram antes, nada no período',
                help: 'Colaboradores com histórico de medição ou treino que não registraram nada entre as duas datas.',
                icon: 'moon',
                accent: 'amber',
                /*
                 * Fecha a conta da base, com os vizinhos: ativos + parados + nunca
                 * registraram = cadastrados. Na base local em 23/09/2026, 31 + 16 + 37 = 84 -
                 * ninguem sobra e ninguem conta duas vezes.
                 *
                 * DOIS recortes num: nada entre as datas, e alguma coisa em algum momento
                 * ate o fim. Sem o segundo, quem nunca registrou entraria junto e a lista
                 * viria maior que o cartao - e a diferenca seria exatamente o vizinho.
                 */
                opens: {
                    hint: 'Ver quem registrou antes e parou',
                    criteria: {
                        activeFrom: inicio,
                        activeTo: fim,
                        activityPresence: 'STALLED',
                        registeredTo: fim
                    }
                }
            }),
            this.buildKpi({
                label: 'Pico de conectados',
                value: formatCount(summary.peakConnectedPerDay),
                subtitle: summary.peakConnectedDate ? `em ${formatDate(summary.peakConnectedDate)}` : undefined,
                help: 'Maior número de pessoas conectadas em um único dia do período.',
                icon: 'trending-up',
                opens: {
                    hint: 'Ver quem registrou algo no dia de pico',
                    criteria: {
                        activeFrom: summary.peakConnectedDate ?? undefined,
                        activeTo: summary.peakConnectedDate ?? undefined
                    },
                    // Sem a data do pico nao ha dia para abrir - e ela falta quando
                    // ninguem se conectou no periodo.
                    when: !!summary.peakConnectedDate
                }
            }),
            this.buildKpi({
                label: 'Novos cadastros',
                value: formatCount(summary.newUsersInPeriod),
                subtitle: 'no período filtrado',
                help: 'Colaboradores cadastrados entre a data inicial e a final.',
                icon: 'user-plus',
                accent: 'amber',
                /*
                 * Este cartao passou a ter caminho em 22/09/2026, e o que mudou foi o
                 * NUMERO e nao o enlace: `sqlRegistrationSeries` contava linhas de
                 * `"user"` e agora conta pessoas COM FICHA. Enquanto contava contas, o
                 * caminho era impossivel - ele dizia 28 e a lista abriria 25, porque as
                 * outras tres eram um super admin, um admin de empresa e um profissional.
                 *
                 * O periodo e o da RESPOSTA, como nos demais: quem conta e o servidor.
                 */
                opens: {
                    hint: 'Ver quem se cadastrou no período',
                    criteria: registrationReach(inicio, fim)
                }
            })
        ];
        this.scope.situationKpis = retrato;
        this.scope.flowKpis = fluxo;
        /*
         * QUATRO marcos, e nao cinco.
         *
         * "Ativos nos ultimos N dias" era o quinto, e fazia a escada CRESCER no fim: com o
         * dado de QA em 18/09/2026 os degraus eram 92, 63, 47, 16 e 31 - a ultima barra com
         * quase o dobro da anterior, num desenho que desce. Ele tambem nao e um marco: os
         * outros quatro sao "ja fez", e esse e "fez nos ultimos N dias" - recencia, e nao
         * comportamento.
         *
         * Nada se perde tirando-o: o mesmo numero ja esta no cartao "Ativos (N dias)", logo
         * acima, com a porcentagem sobre a base ativa.
         */
        const funnel = overview.funnel;
        const steps = [
            /*
             * O degrau conta quem tem cadastro ATIVO - `sqlFunnel` filtra `u.active = true`.
             * Por isso ele nao repete o cartao "Total de colaboradores", que conta todos:
             * 84 contra 88 na base local em 23/09/2026.
             */
            this.buildStep('Cadastrados', funnel?.registered ?? 0, { registeredTo: fim }),
            /*
             * O recorte e o do degrau: cadastrados ate a data de referencia, com conta
             * ativa, ficha COM anamnese. Ele conta pessoas e a lista mostra fichas - ver a
             * nota do cartao "Sem anamnese", que e o outro lado deste mesmo degrau.
             */
            this.buildStep('Preencheram a anamnese', funnel?.withAnamnese ?? 0, {
                registeredTo: fim,
                anamnese: 'FILLED'
            }),
            this.buildStep('Já sincronizaram o wearable', funnel?.everSynced ?? 0, {
                // "JA" e sem comeco, como no degrau de treino - o que muda e nao exigir
                // WORKOUT: aqui vale medicao OU treino, que e o que `everSynced` conta.
                activeTo: fim
            }),
            this.buildStep('Já registraram treino', funnel?.everTrained ?? 0, {
                // "JA" e sem comeco: do primeiro registro ate o fim do periodo.
                activeTo: fim,
                activityKind: 'WORKOUT'
            })
        ];
        // A fracao de cada degrau e sobre o PRIMEIRO, que e a base do funil. Era a view
        // que fazia esta conta; regra do painel, e nao de desenho.
        const base = steps[0]?.value ?? 0;
        for (const step of steps) {
            step.share = base > 0 ? (step.value * 100) / base : 0;
        }
        this.scope.funnel = steps;
        this.scope.overviewSeries = (overview.series ?? []).map((point) => {
            const scope = new OverviewPointScope();
            scope.identity = point.bucket;
            scope.label = point.label;
            scope.newUsers = point.newUsers;
            scope.cumulativeUsers = point.cumulativeUsers;
            scope.connectedUsers = point.connectedUsers;
            scope.dailyAverageConnected = point.dailyAverageConnected;
            scope.activeUsersInWindow = point.activeUsersInWindow;
            scope.update = this.update;
            return scope;
        });
    }
    showTraining(training) {
        const summary = training.summary;
        this.scope.hasTraining = true;
        // Ver `showOverview`: o recorte sai do periodo que a RESPOSTA descreve.
        const inicio = training.period?.startDate ?? this.at.startDate;
        const fim = training.period?.endDate ?? this.at.endDate;
        this.scope.trainingKpis = [
            this.buildKpi({
                label: 'Colaboradores que treinaram',
                value: formatCount(summary.usersWithWorkout),
                subtitle: `em ${formatCount(summary.distinctTypes)} modalidades diferentes`,
                help: 'Colaboradores distintos com pelo menos um treino registrado no período.',
                icon: 'dumbbell',
                opens: {
                    hint: 'Ver quem treinou no período',
                    criteria: {
                        activeFrom: inicio,
                        activeTo: fim,
                        activityKind: 'WORKOUT'
                    }
                }
            }),
            this.buildKpi({
                label: 'Treinos ativados',
                value: formatCount(summary.totalWorkouts),
                subtitle: `${formatDecimal(summary.averageWorkoutsPerUser)} por colaborador`,
                help: 'Total de sessões de treino registradas no período.',
                icon: 'repeat',
                accent: 'emerald'
            }),
            this.buildKpi({
                label: 'Adoção do treino',
                value: formatPercent(summary.workoutAdoptionPercent),
                subtitle: 'dos colaboradores conectados no período',
                help: 'Colaboradores que treinaram divididos pelos que registraram qualquer dado no período.',
                icon: 'percent',
                accent: 'amber'
            }),
            this.buildKpi({
                label: 'Tempo total de treino',
                value: formatDuration(summary.totalDurationSeconds ?? 0),
                subtitle: `${formatKm(summary.totalDistanceMeters)} percorridos`,
                help: 'Soma da duração das sessões registradas.',
                icon: 'timer',
                accent: 'sky'
            })
        ];
        this.scope.trainingTypes = (training.byType ?? []).map((type) => {
            const scope = new TrainingTypeScope();
            scope.identity = type.type;
            scope.type = type.type;
            scope.label = type.label || type.type;
            scope.workouts = type.workouts;
            scope.users = type.users;
            scope.totalDurationSeconds = type.totalDurationSeconds;
            scope.totalSteps = type.totalSteps;
            scope.totalDistanceMeters = type.totalDistanceMeters;
            scope.totalCalories = type.totalCalories;
            scope.averageHeartRate = type.averageHeartRate;
            scope.update = this.update;
            return scope;
        });
        this.scope.trainingSeries = (training.series ?? []).map((point) => {
            const scope = new TrainingPointScope();
            scope.identity = point.bucket;
            scope.label = point.label;
            scope.workouts = point.workouts;
            scope.users = point.users;
            scope.durationSeconds = point.durationSeconds;
            scope.distanceMeters = point.distanceMeters;
            scope.update = this.update;
            return scope;
        });
    }
    showPhysical(physical) {
        const summary = physical.summary;
        this.scope.hasPhysical = true;
        const userDays = summary.stepsUserDays ?? 0;
        this.scope.physicalKpis = [
            this.buildKpi({
                label: 'Passos no período',
                value: formatCount(summary.totalSteps),
                subtitle: `${formatCount(summary.averageStepsPerUserDay)} por colaborador/dia`,
                help: 'Soma de todos os passos registrados no período.',
                icon: 'footprints'
            }),
            this.buildKpi({
                label: 'Distância percorrida',
                value: formatKm(summary.totalDistanceMeters),
                subtitle: `${formatKm(summary.averageDistancePerUserDay)} por colaborador/dia`,
                help: 'Distância registrada pelo wearable, somada no período.',
                icon: 'route',
                accent: 'sky'
            }),
            this.buildKpi({
                label: 'Colaboradores com registro',
                value: formatCount(summary.usersWithSteps),
                subtitle: `${formatCount(userDays)} dias-colaborador com dados`,
                help: 'Colaboradores distintos com pelo menos um dia de passos no período.',
                icon: 'user-check',
                accent: 'emerald'
            }),
            this.buildKpi({
                label: 'Calorias',
                value: formatCount(summary.totalCalories),
                subtitle: `${formatCount(userDays > 0 ? (summary.totalCalories ?? 0) / userDays : 0)} por colaborador/dia`,
                help: 'Soma das calorias registradas pelo wearable no período.',
                icon: 'flame',
                accent: 'amber'
            })
        ];
        this.scope.physicalSeries = (physical.series ?? []).map((point) => {
            const scope = new PhysicalPointScope();
            scope.identity = point.bucket;
            scope.label = point.label;
            scope.steps = point.steps;
            scope.usersWithSteps = point.usersWithSteps;
            scope.averageSteps = point.averageSteps;
            scope.distanceMeters = point.distanceMeters;
            scope.usersWithDistance = point.usersWithDistance;
            scope.averageDistanceMeters = point.averageDistanceMeters;
            scope.calories = point.calories;
            scope.update = this.update;
            return scope;
        });
    }
    /**
     * A bateria dos vestiveis: os quatro cartoes e uma linha por aparelho.
     *
     * Sem resposta - o servidor sem o painel, ou uma falha so dele - a secao some e o resto
     * da tela fica como esta.
     */
    showDevices(devices) {
        this.scope.hasDevices = !!devices;
        if (!devices) {
            this.scope.deviceKpis = [];
            this.scope.deviceSources = [];
            return;
        }
        const sources = devices.bySource ?? [];
        const sum = (field) => sources.reduce((total, source) => total + field(source), 0);
        const band = sources.find((source) => source.measurementSourceId === 103);
        const ring = sources.find((source) => source.measurementSourceId === 104);
        const chargeHelp = '100 dividido pela queda mediana por dia de uso, entre as pessoas com 12 h ou mais de descarga no período.';
        this.scope.deviceKpis = [
            this.buildKpi({
                label: 'Pessoas com leitura',
                value: formatCount(sum((source) => source.users)),
                subtitle: 'pulseira e anel, no período',
                help: 'Pessoas cujo app enviou ao menos uma leitura de bateria do vestível no período.',
                icon: 'watch'
            }),
            this.buildKpi({
                label: 'Bateria baixa',
                value: formatCount(sum((source) => source.lowBatteryUsers)),
                subtitle: 'última leitura em até 20%',
                help: 'Pessoas cuja leitura mais recente do período estava em até 20%, na escala que o app mostra.',
                icon: 'battery-low',
                accent: 'rose'
            }),
            this.buildKpi({
                label: 'Carga da pulseira',
                value: formatDaysPerCharge(band?.medianDaysPerCharge ?? null),
                subtitle: 'duração típica de uma carga',
                help: chargeHelp,
                icon: 'calendar-clock',
                accent: 'emerald'
            }),
            this.buildKpi({
                label: 'Carga do anel',
                value: formatDaysPerCharge(ring?.medianDaysPerCharge ?? null),
                subtitle: 'duração típica de uma carga',
                help: chargeHelp,
                icon: 'ring',
                accent: 'amber'
            })
        ];
        this.scope.deviceSources = sources.map((source) => {
            const scope = new DeviceSourceScope();
            scope.identity = String(source.measurementSourceId);
            scope.measurementSourceId = source.measurementSourceId;
            scope.label = source.label;
            scope.users = source.users;
            scope.readings = source.readings;
            scope.lowBatteryUsers = source.lowBatteryUsers;
            scope.midBatteryUsers = source.midBatteryUsers;
            scope.highBatteryUsers = source.highBatteryUsers;
            scope.usersWithDischarge = source.usersWithDischarge;
            scope.medianDropPerDay = source.medianDropPerDay;
            scope.medianDaysPerCharge = source.medianDaysPerCharge;
            scope.update = this.update;
            return scope;
        });
    }
    buildKpi(spec) {
        const scope = new KpiScope();
        scope.identity = spec.label;
        scope.label = spec.label;
        scope.value = spec.value;
        scope.subtitle = spec.subtitle;
        scope.help = spec.help;
        scope.icon = spec.icon;
        scope.accent = spec.accent ?? 'indigo';
        /*
         * Zero nao leva a lugar nenhum - a mesma guarda das barras do Painel: abrir uma
         * lista que se sabe vazia so tira o leitor de onde ele estava.
         */
        const opens = spec.opens;
        if (opens && opens.when !== false && spec.value !== '0') {
            scope.openHint = opens.hint;
            scope.onOpen = this.action(this.onOpenCollaborators.bind(this, opens.criteria));
        }
        scope.update = this.update;
        return scope;
    }
    /**
     * Abre Colaboradores com o recorte do cartao clicado.
     *
     * `CollaboratorsKeys.of` parte do estado vivo, entao a empresa do CABECALHO vai
     * junto sem ninguem copia-la - o mesmo caminho do Painel e do "Ver Colaboradores"
     * de Empresas. O que este metodo acrescenta sao os dois periodos, que existem no
     * servidor desde que estes cartoes passaram a ter para onde levar, e a empresa
     * escolhida NESTA tela.
     *
     * A empresa precisa ser copiada porque as duas telas a publicam com nomes
     * diferentes - `UserBaseCompany` aqui, `CompanyId` la -, e sao nomes diferentes de
     * proposito: uma e o filtro desta tela e a outra e o filtro daquela, e o mesmo
     * numero com dois donos foi o que fez a troca de empresa abrir a tela na empresa
     * anterior (ver `RestrictedPresenter.companyIdInAddress`). Sem esta copia o cartao
     * dizia 2 para a LVA HEALTH e abria os 31 da base inteira, que e exatamente o susto
     * que estes caminhos existem para evitar.
     *
     * `companyIdToPublish` e nao `at.companyId`: com o cabecalho no comando a empresa
     * ja viaja em `ce`, e escreve-la de novo aqui devolveria aquele bug de dois donos.
     */
    async onOpenCollaborators(reach) {
        const target = CollaboratorsKeys.of(this.app);
        target.companyId = this.companyIdToPublish;
        target.activeFrom = reach.activeFrom;
        target.activeTo = reach.activeTo;
        target.activityKind = reach.activityKind;
        target.activityPresence = reach.activityPresence;
        target.registeredFrom = reach.registeredFrom;
        target.registeredTo = reach.registeredTo;
        target.activeAccountsOnly = reach.activeAccountsOnly ?? SO_CONTAS_ATIVAS;
        target.anamnese = reach.anamnese;
        target.page = 1;
        await target.flip();
    }
    /**
     * A janela de N dias que termina no fim do periodo - a do cartao "Ativos (N)".
     *
     * O fim vem por PARAMETRO, e e o da RESPOSTA e nao o do pedido: quem conta o numero
     * e o servidor, e e o periodo que ELE usou que o recorte precisa repetir. Com
     * `this.at` aqui, um pedido de "30 dias" respondido sobre outro intervalo abriria a
     * lista num recorte que nao e o do cartao.
     */
    activeWindow(days, endDate) {
        return { activeFrom: shiftDays(endDate, -(days - 1)), activeTo: endDate };
    }
    buildStep(label, value, opens) {
        const scope = new FunnelStepScope();
        scope.identity = label;
        scope.label = label;
        scope.value = value;
        if (opens && value > 0) {
            scope.onOpen = this.action(this.onOpenCollaborators.bind(this, opens));
        }
        scope.update = this.update;
        return scope;
    }
    // ========== ACOES ==========
    /**
     * Escolher um atalho recalcula o intervalo e a granularidade.
     *
     * `custom` e o unico que nao recalcula: ele passa a mandar quem manda, e as datas
     * do endereco e que valem.
     */
    async onSelectPreset(preset) {
        const interval = intervalOf(preset);
        if (!interval) {
            await this.go({ preset });
            return;
        }
        await this.go({
            preset,
            startDate: interval.startDate,
            endDate: interval.endDate,
            granularity: interval.granularity
        });
    }
    handleCompanyChanged(value) {
        void this.action(async () => {
            await this.go({ companyId: value === '' ? undefined : Number(value) });
        })();
    }
    /** O "Atualizar": refaz as tres consultas sem mudar filtro algum. */
    // ========== EXPORTACOES ==========
    //
    // O mesmo que a tela mostra, em numeros CRUS (o CSV vai para planilha, e planilha
    // faz conta): segundos, metros, calorias. So as medias levam uma casa decimal.
    async onExportOverview() {
        this.saveCsv('adocao-engajamento-serie-geral.csv', ['Período', 'Novos', 'Acumulado', 'Conectados', 'Média diária', 'Ativos na janela'], this.scope.overviewSeries.map((point) => [
            point.label,
            point.newUsers,
            point.cumulativeUsers,
            point.connectedUsers,
            csvDecimal(point.dailyAverageConnected),
            point.activeUsersInWindow
        ]));
    }
    async onExportTrainingTypes() {
        this.saveCsv('adocao-engajamento-modalidades.csv', ['Modalidade', 'Treinos', 'Colaboradores', 'Duração (s)', 'Distância (m)', 'Calorias', 'FC média'], this.scope.trainingTypes.map((kind) => [
            kind.label,
            kind.workouts,
            kind.users,
            kind.totalDurationSeconds,
            kind.totalDistanceMeters,
            kind.totalCalories,
            csvDecimal(kind.averageHeartRate)
        ]));
    }
    async onExportTrainingSeries() {
        this.saveCsv('adocao-engajamento-serie-treinos.csv', ['Período', 'Treinos', 'Colaboradores', 'Duração (s)', 'Distância (m)'], this.scope.trainingSeries.map((point) => [
            point.label,
            point.workouts,
            point.users,
            point.durationSeconds,
            point.distanceMeters
        ]));
    }
    async onExportPhysical() {
        this.saveCsv('adocao-engajamento-serie-fisica.csv', ['Período', 'Passos', 'Média de passos', 'Distância (km)', 'Média de distância (km)', 'Calorias'], this.scope.physicalSeries.map((point) => [
            point.label,
            point.steps,
            csvDecimal(point.averageSteps),
            csvDecimal(point.distanceMeters / 1000),
            csvDecimal(point.averageDistanceMeters / 1000),
            point.calories
        ]));
    }
    async onExportDevices() {
        this.saveCsv('adocao-engajamento-bateria-dos-vestiveis.csv', [
            'Vestível',
            'Pessoas com leitura',
            'Leituras',
            'Última leitura até 20%',
            'Entre 21% e 50%',
            'Acima de 50%',
            'Pessoas na mediana',
            'Queda por dia (pontos)',
            'Duração de uma carga (dias)'
        ], this.scope.deviceSources.map((source) => [
            source.label,
            source.users,
            source.readings,
            source.lowBatteryUsers,
            source.midBatteryUsers,
            source.highBatteryUsers,
            source.usersWithDischarge,
            csvDecimal(source.medianDropPerDay),
            csvDecimal(source.medianDaysPerCharge)
        ]));
    }
    saveCsv(name, header, rows) {
        FileSaver.INSTANCE.save({ name, content: csvBlob(header, rows) });
    }
    async onRefresh() {
        this.lastQuery = undefined;
        await this.moveTo(this.at);
    }
    handleGranularityChanged(value) {
        if (value === 'DAY' || value === 'MONTH') {
            void this.action(async () => {
                await this.go({ granularity: value });
            })();
        }
    }
}
//# sourceMappingURL=user-base.presenter.js.map